import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { usePageSeo } from '../../utils/seo';

export default function Terms() {
  usePageSeo({
    title: 'Пользовательское соглашение | Promokod.online',
    description: 'Пользовательское соглашение Promokod.online: условия использования сервиса, права и обязанности пользователей и администрации сайта.',
    canonicalPath: '/terms',
    noIndex: true,
  });

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-1 pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Пользовательское соглашение
            </h1>
            <p className="text-gray-600">
              Последнее обновление: 15 января 2025 года
            </p>
          </div>

          {/* Content */}
          <div className="space-y-8 text-gray-700 leading-relaxed">
            {/* Section 1 */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                1. Общие положения
              </h2>
              <p className="mb-3">
                Настоящее Пользовательское соглашение (далее — «Соглашение») регулирует отношения между администрацией сервиса Promokod.online (далее — «Администрация», «Сервис») и пользователями сервиса (далее — «Пользователь»).
              </p>
              <p className="mb-3">
                Сервис Promokod.online представляет собой онлайн-платформу для поиска и использования промокодов, скидок и специальных предложений от различных интернет-магазинов и сервисов.
              </p>
              <p className="mb-3">
                Начиная использовать Сервис, Пользователь считается принявшим условия настоящего Соглашения в полном объёме, без всяких оговорок и исключений. В случае несогласия с условиями Соглашения использование Сервиса должно быть немедленно прекращено.
              </p>
              <p>
                Администрация вправе в любое время изменять условия настоящего Соглашения без предварительного уведомления Пользователя. Актуальная версия Соглашения размещается на данной странице.
              </p>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                2. Условия использования сервиса
              </h2>
              <p className="mb-3">
                <strong>2.1.</strong> Сервис предоставляется на условиях «как есть». Администрация не гарантирует, что Сервис будет соответствовать всем требованиям Пользователя, работать непрерывно, быстро, надёжно и без ошибок.
              </p>
              <p className="mb-3">
                <strong>2.2.</strong> Для использования некоторых функций Сервиса может потребоваться регистрация. При регистрации Пользователь обязуется предоставить достоверную и полную информацию о себе.
              </p>
              <p className="mb-3">
                <strong>2.3.</strong> Пользователь несёт ответственность за сохранность своих учётных данных (логин и пароль) и не вправе передавать их третьим лицам.
              </p>
              <p className="mb-3">
                <strong>2.4.</strong> Все промокоды и предложения, размещённые на Сервисе, предоставляются партнёрами и магазинами. Администрация не несёт ответственности за актуальность, работоспособность и условия использования промокодов.
              </p>
              <p className="mb-3">
                <strong>2.5.</strong> Администрация не является продавцом товаров или услуг, представленных на Сервисе, и не несёт ответственности за качество товаров, сроки доставки и другие аспекты сделок между Пользователем и магазинами.
              </p>
              <p>
                <strong>2.6.</strong> Пользователь обязуется использовать Сервис исключительно в законных целях и не нарушать права третьих лиц.
              </p>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                3. Права и обязанности сторон
              </h2>
              <div className="mb-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  3.1. Администрация имеет право:
                </h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Изменять или дополнять условия настоящего Соглашения</li>
                  <li>Ограничивать или прекращать доступ Пользователя к Сервису при нарушении условий Соглашения</li>
                  <li>Удалять учётные записи Пользователей, нарушающих правила использования Сервиса</li>
                  <li>Изменять функционал Сервиса, добавлять или удалять разделы и функции</li>
                  <li>Приостанавливать работу Сервиса для проведения технических работ</li>
                </ul>
              </div>
              <div className="mb-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  3.2. Администрация обязуется:
                </h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Предоставлять Пользователю доступ к функционалу Сервиса</li>
                  <li>Обеспечивать конфиденциальность персональных данных Пользователя в соответствии с Политикой конфиденциальности</li>
                  <li>Стремиться к актуализации информации о промокодах и предложениях</li>
                  <li>Рассматривать обращения Пользователей в разумные сроки</li>
                </ul>
              </div>
              <div className="mb-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  3.3. Пользователь имеет право:
                </h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Использовать все доступные функции Сервиса</li>
                  <li>Получать информацию о промокодах и специальных предложениях</li>
                  <li>Обращаться в службу поддержки по вопросам работы Сервиса</li>
                  <li>Удалить свою учётную запись в любое время</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  3.4. Пользователь обязуется:
                </h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Соблюдать условия настоящего Соглашения</li>
                  <li>Предоставлять достоверную информацию при регистрации</li>
                  <li>Не использовать Сервис в противоправных целях</li>
                  <li>Не нарушать работу Сервиса и не предпринимать попытки несанкционированного доступа</li>
                  <li>Не размещать материалы, нарушающие законодательство или права третьих лиц</li>
                  <li>Уважать права интеллектуальной собственности Администрации и третьих лиц</li>
                </ul>
              </div>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                4. Ответственность сторон
              </h2>
              <p className="mb-3">
                <strong>4.1.</strong> Администрация не несёт ответственности за:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-3">
                <li>Неработоспособность или недействительность промокодов, размещённых на Сервисе</li>
                <li>Качество товаров и услуг, приобретённых Пользователем в магазинах-партнёрах</li>
                <li>Убытки, понесённые Пользователем в результате использования или невозможности использования Сервиса</li>
                <li>Действия третьих лиц, направленные на нарушение работы Сервиса</li>
                <li>Несанкционированный доступ к учётной записи Пользователя по причине разглашения им своих учётных данных</li>
              </ul>
              <p className="mb-3">
                <strong>4.2.</strong> Пользователь несёт полную ответственность за:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-3">
                <li>Достоверность предоставленной при регистрации информации</li>
                <li>Сохранность своих учётных данных</li>
                <li>Все действия, совершённые под его учётной записью</li>
                <li>Нарушение прав третьих лиц при использовании Сервиса</li>
              </ul>
              <p>
                <strong>4.3.</strong> В случае нарушения Пользователем условий настоящего Соглашения Администрация вправе заблокировать или удалить учётную запись Пользователя без предварительного уведомления.
              </p>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                5. Интеллектуальная собственность
              </h2>
              <p className="mb-3">
                <strong>5.1.</strong> Все объекты, размещённые на Сервисе, включая элементы дизайна, текст, графические изображения, иллюстрации, видео, программы, музыку, звуки и другие объекты (далее — «Контент»), являются объектами исключительных прав Администрации и других правообладателей.
              </p>
              <p className="mb-3">
                <strong>5.2.</strong> Использование Контента, а также каких-либо иных элементов Сервиса возможно только в рамках функционала, предлагаемого Сервисом. Никакие элементы содержания Сервиса не могут быть использованы иным образом без предварительного разрешения Администрации.
              </p>
              <p className="mb-3">
                <strong>5.3.</strong> Под использованием подразумеваются, в том числе: воспроизведение, копирование, переработка, распространение на любой основе, отображение во фрейме и т.д.
              </p>
              <p>
                <strong>5.4.</strong> Использование Пользователем элементов содержания Сервиса для личного некоммерческого использования допускается при условии сохранения всех знаков охраны авторского права, смежных прав, товарных знаков, других уведомлений об авторстве.
              </p>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                6. Разрешение споров
              </h2>
              <p className="mb-3">
                <strong>6.1.</strong> В случае возникновения споров или разногласий, связанных с использованием Сервиса, Пользователь и Администрация приложат все усилия для их разрешения путём переговоров.
              </p>
              <p className="mb-3">
                <strong>6.2.</strong> При невозможности разрешения спора в досудебном порядке, спор подлежит рассмотрению в судебном порядке в соответствии с действующим законодательством Российской Федерации.
              </p>
              <p>
                <strong>6.3.</strong> К настоящему Соглашению и отношениям между Пользователем и Администрацией применяется законодательство Российской Федерации.
              </p>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                7. Заключительные положения
              </h2>
              <p className="mb-3">
                <strong>7.1.</strong> Настоящее Соглашение вступает в силу с момента начала использования Сервиса Пользователем и действует бессрочно.
              </p>
              <p className="mb-3">
                <strong>7.2.</strong> Пользователь вправе в любой момент прекратить использование Сервиса и удалить свою учётную запись.
              </p>
              <p className="mb-3">
                <strong>7.3.</strong> Все уведомления, связанные с исполнением настоящего Соглашения, направляются в письменной форме по электронной почте или через интерфейс Сервиса.
              </p>
              <p className="mb-3">
                <strong>7.4.</strong> Если какое-либо из положений настоящего Соглашения будет признано недействительным, это не влияет на действительность остальных положений.
              </p>
              <p>
                <strong>7.5.</strong> Бездействие со стороны Администрации в случае нарушения Пользователем положений Соглашения не лишает Администрацию права предпринять соответствующие действия в защиту своих интересов позднее.
              </p>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                8. Контактная информация
              </h2>
              <p className="mb-3">
                По всем вопросам, связанным с настоящим Соглашением, вы можете обратиться к нам:
              </p>
              <ul className="space-y-2">
                <li>
                  <strong>Email:</strong> <a href="mailto:support@promokod.online" className="text-blue-600 hover:underline">support@promokod.online</a>
                </li>
                <li>
                  <strong>Адрес:</strong> Россия, Москва
                </li>
              </ul>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
